import { format, parseISO, startOfToday } from "date-fns"
import PropTypes from "prop-types"
import React, { forwardRef } from "react"
import { useMutation, useQueryClient } from "react-query"
import { useMarina } from "src/contexts/MarinaContext"

import Button from "src/components/Button"
import Form from "src/components/Form"

import { scheduleWorkOrder } from "src/api/Service/WorkOrders"

import { useToast } from "src/hooks/use_toast"

const ScheduleButton = forwardRef(({ onClick, label }, ref) => (
  <Button
    ref={ref}
    variant="secondary"
    icon="fa-calendar-month"
    onClick={onClick}
  >
    {label}
  </Button>
))

ScheduleButton.displayName = "ScheduleButton"

ScheduleButton.propTypes = {
  onClick: PropTypes.func,
  label: PropTypes.string.isRequired,
}

const ScheduleTrigger = ({ workOrder, workOrderSequentialId }) => {
  const { marinaSlug } = useMarina()
  const showToast = useToast()
  const queryClient = useQueryClient()

  const isReschedule = Boolean(workOrder.scheduledAt)
  const label = isReschedule ? "Reschedule" : "Schedule"
  const selectedDate = workOrder.scheduledAt
    ? parseISO(workOrder.scheduledAt)
    : null

  const { mutate } = useMutation(
    (date) =>
      scheduleWorkOrder(marinaSlug, workOrderSequentialId, {
        scheduledAt: format(date, "yyyy-MM-dd"),
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([
          "workOrder",
          marinaSlug,
          workOrderSequentialId,
        ])
        queryClient.invalidateQueries(["workOrders", marinaSlug])
        showToast(
          isReschedule ? "Work order rescheduled" : "Work order scheduled",
          { type: "success" }
        )
      },
      onError: (error) => {
        showToast(error.message || "Failed to schedule work order.", {
          type: "error",
        })
      },
    }
  )

  const handleChange = (date) => {
    if (!date) return
    mutate(date)
  }

  return (
    <Form.DatePicker
      id={`schedule-work-order-${workOrderSequentialId}`}
      value={selectedDate}
      onChange={handleChange}
      minDate={startOfToday()}
      customInput={<ScheduleButton label={label} />}
    />
  )
}

ScheduleTrigger.propTypes = {
  workOrder: PropTypes.shape({
    scheduledAt: PropTypes.string,
  }).isRequired,
  workOrderSequentialId: PropTypes.string.isRequired,
}

export default ScheduleTrigger
